import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createUserSession,
  generateShortcode,
  getUserSession,
  recordAdvertisingClick
} from '../services/funnelService';
import {
  getShortcodeFromStorage,
  saveUserSessionToStorage
} from '../services/browserStorageService';

const MARKETING_BASE_URL = 'https://www.thrivewithdory.com/';
const ESCAPE_NAVIGATE_MS = 2500;

/**
 * Resolve inbound query params for /fwd.
 *
 * An unencoded "#" in a query value (e.g. ?path=#pricing&utm_source=fb) is
 * treated by the browser as the document fragment, so location.search becomes
 * "?path=" and location.hash becomes "#pricing&utm_source=fb". Reconstruct
 * the intended params in that case. Prefer %23encoding: ?path=%23pricing
 */
const getForwardParams = () => {
  const params = new URLSearchParams(window.location.search);
  const pathFromSearch = params.get('path');
  const hash = window.location.hash;

  if (pathFromSearch && pathFromSearch.trim()) {
    return params;
  }

  if (!hash || hash === '#') {
    return params;
  }

  const hashBody = hash.slice(1);
  const ampIndex = hashBody.indexOf('&');
  const fragmentTarget = ampIndex === -1 ? hashBody : hashBody.slice(0, ampIndex);
  const remainderQuery = ampIndex === -1 ? '' : hashBody.slice(ampIndex + 1);

  // ?path= was emptied by an unencoded "#..."
  if (params.has('path') && !pathFromSearch?.trim() && fragmentTarget) {
    params.set('path', `#${fragmentTarget}`);
  } else if (!params.has('path') && fragmentTarget && !fragmentTarget.includes('=')) {
    // /fwd#pricing or /fwd?#pricing
    params.set('path', `#${fragmentTarget}`);
  }

  if (remainderQuery) {
    const extra = new URLSearchParams(remainderQuery);
    extra.forEach((value, key) => {
      if (!params.has(key) || !params.get(key)) {
        params.set(key, value);
      }
    });
  }

  return params;
};

const buildExternalDestinationUrl = (params) => {
  const rawPath = (params.get('path') || '').trim();
  let destination;

  if (!rawPath) {
    destination = new URL(MARKETING_BASE_URL);
  } else if (rawPath.startsWith('#')) {
    destination = new URL(MARKETING_BASE_URL);
    destination.hash = rawPath;
  } else {
    const pathSegment = rawPath.replace(/^\/+/, '');
    destination = new URL(pathSegment, MARKETING_BASE_URL);
  }

  params.forEach((value, key) => {
    if (key === 'path' || key === 'key') {
      return;
    }
    destination.searchParams.append(key, value);
  });

  return destination.toString();
};

const buildForwardSearch = (params) => {
  const next = new URLSearchParams();
  params.forEach((value, key) => {
    if (key === 'path' || key === 'key') {
      return;
    }
    next.append(key, value);
  });
  const serialized = next.toString();
  return serialized ? `?${serialized}` : '';
};

const buildOptimisticSession = (shortcode) => {
  const now = new Date().toISOString();
  return {
    shortcode,
    startTime: now,
    lastActive: now,
    currentScreen: null,
    completedScreens: [],
    userInfo: {},
  };
};

/**
 * Ensure we have a shortcode synchronously (localStorage or freshly generated).
 * Firestore create + attribution run in the background and must not block navigation.
 */
const ensureOptimisticShortcode = () => {
  const storedShortcode = getShortcodeFromStorage();
  const shortcode = storedShortcode || generateShortcode();
  const session = buildOptimisticSession(shortcode);
  saveUserSessionToStorage(session);
  return session;
};

const persistSessionAndAttribution = async (shortcode, params, landingPage) => {
  let session = await getUserSession(shortcode);
  if (!session) {
    session = await createUserSession(shortcode);
  }
  saveUserSessionToStorage(session);
  await recordAdvertisingClick(session.shortcode, {
    params,
    landingPage,
    referrer: document.referrer,
  });
  return session;
};

const ForwardRedirect = () => {
  const navigate = useNavigate();
  const didRun = useRef(false);
  const [statusMessage, setStatusMessage] = useState('Taking you there…');

  useEffect(() => {
    if (didRun.current) {
      return;
    }
    didRun.current = true;

    const params = getForwardParams();
    const rawKey = (params.get('key') || '').trim();
    const session = ensureOptimisticShortcode();
    const search = buildForwardSearch(params);

    let landingPage;
    let navigated = false;

    const goInApp = () => {
      if (navigated) return;
      navigated = true;
      const keySegment = rawKey.replace(/^\/+/, '');
      navigate(
        {
          pathname: `/optimalpace/${session.shortcode}/${keySegment}`,
          search,
        },
        { replace: true }
      );
    };

    const goExternal = (url) => {
      if (navigated) return;
      navigated = true;
      window.location.replace(url);
    };

    if (rawKey) {
      landingPage = `${window.location.origin}/optimalpace/${session.shortcode}/${rawKey.replace(/^\/+/, '')}${search}`;

      // Soft navigate immediately — no second SPA boot for Instagram WebViews
      goInApp();

      // Background: ensure Firestore user + attribution (never blocks first paint)
      persistSessionAndAttribution(session.shortcode, params, landingPage).catch((error) => {
        console.error('ForwardRedirect: background session/attribution failed', error);
      });
    } else {
      landingPage = buildExternalDestinationUrl(params);

      // Escape hatch if Firestore hangs before external redirect
      const escapeTimer = setTimeout(() => {
        setStatusMessage('Still working… continuing now');
        goExternal(landingPage);
      }, ESCAPE_NAVIGATE_MS);

      persistSessionAndAttribution(session.shortcode, params, landingPage)
        .catch((error) => {
          console.error('ForwardRedirect: failed to ensure session or save attribution', error);
        })
        .finally(() => {
          clearTimeout(escapeTimer);
          goExternal(landingPage);
        });
    }
  }, [navigate]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#3B515D',
        textAlign: 'center',
      }}
    >
      {statusMessage}
    </div>
  );
};

export default ForwardRedirect;
